import React, { Fragment, useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Label, Button } from 'reactstrap';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import SubjectIcon from '@material-ui/icons/Subject';
import MaterialTable from 'material-table';
import ThumbsUpDownIcon from '@material-ui/icons/ThumbsUpDown';
import { TextareaAutosize } from '@material-ui/core';
import { v4 as uuid } from 'uuid';
import renderField from '../../../shared/components/form/RenderField';
import SmartHtmlEditor from '../../../shared/components/form/SmartHtmlEditor';
import { QUESTION_TYPE } from '../../../shared/utils/constants';
import Select from '../../../shared/components/form/Select';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import columns from './AnswersColumn';
import materialTableIcons from '../../../shared/utils/materialTableIcons';

const QuestionFormFields = (props) => {
  const {
    t,
    dto,
    onDescriptionChange,
    answerUpdateHandler,
    showTitle,
    showTag,
    showActive,
    subTitle,
    addQuestion,
  } = props;

  const [answers, setAnswers] = useState([]);
  const [questionType, setQuestionType] = useState();
  const [answerToMaintain, setAnswerToMaintain] = useState({ description: '' });

  const typesDescriptions = [
    {
      value: QUESTION_TYPE.OBJECTIVE,
      label: t(`question.type-description.${QUESTION_TYPE.OBJECTIVE}`),
      icon: <RadioButtonCheckedIcon />,
    },
    {
      value: QUESTION_TYPE.SELECTION,
      label: t(`question.type-description.${QUESTION_TYPE.SELECTION}`),
      icon: <CheckBoxIcon />,
    },
    {
      value: QUESTION_TYPE.DESCRIPTIVE,
      label: t(`question.type-description.${QUESTION_TYPE.DESCRIPTIVE}`),
      icon: <SubjectIcon />,
    },
    {
      value: QUESTION_TYPE.TRUE_FALSE,
      label: t(`question.type-description.${QUESTION_TYPE.TRUE_FALSE}`),
      icon: <ThumbsUpDownIcon />,
    },
  ];

  const questionTypeChangeHandler = (value) => {
    if (answers) {
      if (value.value === QUESTION_TYPE.SELECTION) {
        const updatedAnswers = answers.map((item) => ({
          ...item,
          correct: false,
        }));
        setAnswers(updatedAnswers);
      }
    }
    setQuestionType(value.value);
  };

  const updateAnswerHandler = (data, newData) => {
    if (questionType === QUESTION_TYPE.OBJECTIVE) {
      const updatedAnswers = answers.map((item) => ({
        ...item,
        correct: newData.id === item.id ? newData.correct : false,
      }));
      setAnswers(updatedAnswers);
    } else {
      const updatedAnswers = answers.map((item) => ({
        ...item,
        trueOrFalse:
          newData.id === item.id ? newData.trueOrFalse : item.trueOrFalse,
        correct: newData.id === item.id ? newData.correct : item.correct,
      }));
      setAnswers(updatedAnswers);
    }
  };

  const removeQuestionHandler = (evt, rowData) => {
    const updatedAnswers = answers.filter((item) => item.id !== rowData.id);
    setAnswers(updatedAnswers);
  };

  const addQuestionHandler = () => {
    if (answerToMaintain.description) {
      const updatedAnswers = answers ? [...answers] : [];
      if (answerToMaintain.id) {
        const answersIndex = updatedAnswers.findIndex(
          (item) => item.id === answerToMaintain.id
        );
        updatedAnswers[answersIndex] = { ...answerToMaintain };
      } else {
        updatedAnswers.push({
          ...answerToMaintain,
          id: uuid(),
          correct: false,
        });
      }
      setAnswers(updatedAnswers);
      setAnswerToMaintain({ id: 0, description: '' });
    }
  };

  const onAnswerDescriptionChange = (event) => {
    const updatingAnswer = { ...answerToMaintain };
    updatingAnswer.description = event.target.value;
    setAnswerToMaintain(updatingAnswer);
  };

  const onAnswerEditClickHandler = (event, rowData) => {
    setAnswerToMaintain({ ...rowData });
  };

  useEffect(() => {
    setAnswers(dto.answers);
    if (dto.type) {
      setQuestionType(dto.type.value);
    }
  }, [dto]);

  const onAddQuestionHandler = () => {
    const question = {
      type: questionType,
      answers,
    };
    addQuestion(question);
  };

  useEffect(() => {
    answerUpdateHandler(answers);
  }, [answers]);

  return (
    <Fragment>
      {showTitle ? (
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.title')}</span>
          <div className="form_form-group-field">
            <Field
              name="title"
              type="text"
              component={renderField}
              maxLength="250"
              placeholder={t('question.placeholders.title')}
            />
          </div>
        </div>
      ) : (
        <div className="card__title">
          <h5 className="bold-text">{subTitle}</h5>
        </div>
      )}
      <div className="form__form-group">
        <span className="form__form-group-label">{t('question.question')}</span>
        <div className="form_form-group-field">
          <Field
            name="description"
            component={SmartHtmlEditor}
            onEditorChange={onDescriptionChange}
            htmlValue={dto.description}
          />
        </div>
      </div>
      <div className="form__form-group">
        <span className="form__form-group-label">
          {t('question.answer-type')}
        </span>
        <div className="form_form-group-field">
          <Field
            name="type"
            type="text"
            component={Select}
            options={typesDescriptions}
            placeholder={t('question.placeholders.type')}
            onChange={questionTypeChangeHandler}
          />
        </div>
      </div>
      {!!questionType && questionType !== QUESTION_TYPE.DESCRIPTIVE ? (
        <Fragment>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.options')}
            </span>
            <div className="form__form-group-field col-md-12 col-lg-12">
              <TextareaAutosize
                value={answerToMaintain.description}
                onChange={onAnswerDescriptionChange}
              />
              <div>
                <IconButton
                  aria-label="add"
                  className="vertical__align__middle"
                  onClick={addQuestionHandler}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </div>
          <div className="form__form-group">
            <MaterialTable
              data={answers}
              icons={materialTableIcons}
              columns={columns(t, updateAnswerHandler, questionType)}
              actions={[
                {
                  icon: 'delete',
                  tooltip: t('general.tables.delete_registry'),
                  onClick: (evt, rowData) =>
                    removeQuestionHandler(evt, rowData),
                },
                {
                  icon: 'edit',
                  tooltip: t('general.tables.change_registry'),
                  onClick: (evt, rowData) =>
                    onAnswerEditClickHandler(evt, rowData),
                },
              ]}
              options={{
                paging: false,
                showTitle: false,
                search: false,
                toolbar: false,
                actionsColumnIndex: 8,
              }}
              localization={{
                pagination: {
                  labelRowsSelect: t('general.tables.rows'),
                  labelDisplayedRows: `{from}-{to} ${t(
                    'general.tables.labelDisplayRows'
                  )} {count}`,
                  firstTooltip: t('general.tables.firstTooltip'),
                  previousTooltip: t('general.tables.firstTooltip'),
                  nextTooltip: t('general.tables.firstTooltip'),
                  lastTooltip: t('general.tables.firstTooltip'),
                },

                header: {
                  actions: t('general.tables.actions'),
                },
                toolbar: {
                  exportTitle: t('general.tables.export_title'),
                  exportAriaLabel: t('general.tables.export'),
                  exportCSVName: t('general.tables.exportCSVName'),
                  exportPDFName: t('general.tables.exportPDFName'),
                  searchPlaceholder: t('general.tables.search_placeholder'),
                  nRowsSelected: `{0} ${t('general.tables.nRowsSelected')}`,
                },
                body: {
                  emptyDataSourceMessage: t(
                    'general.tables.no-data-to-display'
                  ),
                  exportTitle: t('general.tables.export_title'),
                  exportAriaLabel: t('general.export'),
                  exportCSVName: t('general.tables.exportCSVName'),
                  exportPDFName: t('general.exportPDFName'),
                  searchPlaceholder: t('general.tables.search_placeholder'),
                },
              }}
            />
          </div>
        </Fragment>
      ) : null}
      {showTag ? (
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.tags')}</span>
          <div className="form_form-group-field">
            <Field
              name="tags"
              type="text"
              component={renderField}
              maxLength="250"
              placeholder={t('question.placeholders.tags')}
            />
          </div>
        </div>
      ) : null}
      {showActive ? (
        <div className="form__form-group">
          <div className="form__form-group-field">
            <Label className="generic__label">{t('general.active')}</Label>
            <Field
              name="active"
              component={renderCheckBoxField}
              className="colored-click"
            />
          </div>
        </div>
      ) : null}
      <div className="form__form-group">
        <div className="question-buttons">
          <Button onClick={onAddQuestionHandler}>Adicionar Questão</Button>
          <Button>Buscar No Banco de Questões</Button>
        </div>
      </div>
    </Fragment>
  );
};

QuestionFormFields.propTypes = {
  t: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  answerUpdateHandler: PropTypes.func.isRequired,
  addQuestion: PropTypes.func,
  showTitle: PropTypes.bool,
  showTag: PropTypes.bool,
  showActive: PropTypes.bool,
  subTitle: PropTypes.string,
};

QuestionFormFields.defaultProps = {
  showTitle: true,
  showTag: true,
  showActive: true,
  addQuestion: null,
  subTitle: '',
};

export default withTranslation('common')(QuestionFormFields);
